<div class="webhooks-page-container">
  @if (initialLoadFailed() && loadError(); as error) {
    <tf-settings-load-error
      [title]="error"
      [message]="'SETTINGS.LOAD_ERROR.MESSAGE' | translate"
      [details]="loadErrorDetails()"
      [dense]="true"
      [retrying]="isLoading()"
      (retry)="reloadEndpoints()" />
  } @else if (!hasLoadedOnce()) {
    <tf-settings-loading-placeholder [rows]="4" [columns]="columns.length" />
  } @else if (showEmptyState()) {
    <tf-settings-inline-message
      [message]="actionError()"
      messageId="webhooks-action-error"
      (dismissed)="clearActionError()" />

    <tf-empty-state
      [title]="'SETTINGS_WEBHOOKS.EMPTY.TITLE' | translate"
      [body]="'SETTINGS_WEBHOOKS.EMPTY.BODY' | translate">
      <svg
        empty-state-illustration
        class="settings-empty-illustration webhooks-empty-illustration"
        width="96"
        height="96"
        viewBox="0 0 100 100"
        fill="none"
        aria-hidden="true">
        <path class="webhooks-empty-illustration__flow" d="M30 30H48C56 30 60 35 60 43V58" />
        <path class="webhooks-empty-illustration__flow" d="M30 70H48C56 70 60 65 60 57" />
        <path class="webhooks-empty-illustration__flow" d="M62 50H80" />
        <rect
          class="webhooks-empty-illustration__node"
          x="12"
          y="18"
          width="28"
          height="24"
          rx="6" />
        <rect
          class="webhooks-empty-illustration__node"
          x="12"
          y="58"
          width="28"
          height="24"
          rx="6" />
        <rect
          class="webhooks-empty-illustration__node webhooks-empty-illustration__node--primary"
          x="52"
          y="38"
          width="28"
          height="24"
          rx="6" />
        <circle class="webhooks-empty-illustration__dot" cx="24" cy="30" r="3" />
        <circle class="webhooks-empty-illustration__dot" cx="33" cy="30" r="3" />
        <path class="webhooks-empty-illustration__bolt" d="M64 44L59 52H66L61 60" />
        <path class="webhooks-empty-illustration__arrow" d="M78 44L86 50L78 56" />
      </svg>
      <p-button
        empty-state-actions
        icon="pi pi-plus"
        [label]="'SETTINGS_WEBHOOKS.ADD_ENDPOINT' | translate"
        [disabled]="!canCreateWebhook()"
        [pTooltip]="canCreateWebhook() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
        tooltipPosition="top"
        (onClick)="createEndpoint()" />
      <p-button
        empty-state-actions
        icon="pi pi-refresh"
        severity="secondary"
        [loading]="isLoading()"
        [disabled]="isLoading()"
        [ariaLabel]="'GENERAL.REFRESH' | translate"
        (onClick)="reloadEndpoints()" />
    </tf-empty-state>
  } @else {
    <tf-settings-list-header
      [showSearch]="false"
      [loading]="isLoading()"
      (refresh)="reloadEndpoints()">
      <p-button
        headerActions
        [label]="'SETTINGS_WEBHOOKS.ADD_ENDPOINT' | translate"
        icon="pi pi-plus"
        severity="primary"
        [disabled]="!canCreateWebhook()"
        [pTooltip]="canCreateWebhook() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
        tooltipPosition="top"
        (onClick)="createEndpoint()" />
    </tf-settings-list-header>

    <tf-settings-inline-message
      [message]="actionError()"
      messageId="webhooks-action-error"
      (dismissed)="clearActionError()" />

    <tf-data-table
      [columns]="columns"
      [value]="endpoints()"
      [loading]="false"
      [paginator]="true"
      emptyMessageKey="SETTINGS_WEBHOOKS.NO_ENDPOINTS_FOUND">
      <ng-template tfColumnDef="displayName" let-endpoint>
        <span class="endpoint-name-text">
          <button
            type="button"
            class="endpoint-id-link"
            [pTooltip]="endpoint.id"
            tooltipPosition="top"
            aria-label="Copy endpoint id"
            (click)="copyEndpointId(endpoint, $event)">
            #ID
          </button>
          {{ endpoint.displayName || '-' }}
        </span>
      </ng-template>
      <ng-template tfColumnDef="enabled" let-endpoint>
        <p-tag
          class="enabled-tag"
          [value]="
            endpoint.enabled
              ? ('SETTINGS_WEBHOOKS.ENABLED' | translate)
              : ('SETTINGS_WEBHOOKS.DISABLED' | translate)
          "
          [severity]="endpoint.enabled ? 'success' : 'danger'" />
      </ng-template>
      <ng-template tfColumnDef="retry" let-endpoint>
        <div class="flex align-items-center gap-2">
          <p-tag
            class="retry-tag"
            [value]="
              endpoint.retry
                ? ('SETTINGS_WEBHOOKS.ENABLED' | translate)
                : ('SETTINGS_WEBHOOKS.DISABLED' | translate)
            "
            [severity]="endpoint.retry ? 'success' : 'secondary'" />
          <i
            class="pi pi-info-circle text-color-secondary cursor-pointer"
            [pTooltip]="'SETTINGS_WEBHOOKS.RETRY_TOOLTIP' | translate"
            tooltipPosition="top"></i>
        </div>
      </ng-template>
      <ng-template tfColumnDef="_eventTypes" let-endpoint>
        <span class="event-types-summary">
          {{ eventTypesSummaryByEndpointId().get(endpoint.id) ?? '' }}
          @if (hasInvalidSubscriptionsByEndpointId().get(endpoint.id)) {
            <i
              class="pi pi-exclamation-triangle invalid-subscription-icon"
              [pTooltip]="'SETTINGS_WEBHOOKS.INVALID_SUBSCRIPTIONS_WARNING' | translate"
              tooltipPosition="top"></i>
          }
        </span>
      </ng-template>
      <ng-template tfColumnDef="_testStatus" let-endpoint>
        @if (testingEndpointId() === endpoint.id) {
          <i class="pi pi-spin pi-spinner"></i>
        } @else {
          @let health = testHealthByEndpointId().get(endpoint.id);
          @if (health?.status === 'success') {
            <i
              class="pi pi-check-circle text-2xl"
              [style.color]="'var(--green)'"
              [pTooltip]="health?.tooltip ?? ''"
              tooltipPosition="top"></i>
          } @else if (health?.status === 'failed') {
            <i
              class="pi pi-exclamation-triangle text-2xl"
              [style.color]="'var(--red)'"
              [pTooltip]="health?.tooltip ?? ''"
              tooltipPosition="top"></i>
          } @else {
            <span class="text-color-secondary">-</span>
          }
        }
      </ng-template>
      <ng-template tfColumnDef="_actions" let-endpoint>
        <div class="action-buttons">
          <p-button
            icon="pi pi-send"
            severity="secondary"
            [text]="true"
            [rounded]="true"
            size="small"
            [disabled]="!canTestWebhook()"
            [loading]="testingEndpointId() === endpoint.id"
            [pTooltip]="
              canTestWebhook()
                ? ('SETTINGS_WEBHOOKS.TEST_WEBHOOK' | translate)
                : ('GENERAL.NO_PERMISSION' | translate)
            "
            tooltipPosition="top"
            (onClick)="testEndpoint(endpoint)" />
          <p-button
            icon="pi pi-pencil"
            severity="secondary"
            [text]="true"
            [rounded]="true"
            size="small"
            [disabled]="!canUpdateWebhook()"
            [pTooltip]="
              canUpdateWebhook()
                ? ('GENERAL.EDIT' | translate)
                : ('GENERAL.NO_PERMISSION' | translate)
            "
            tooltipPosition="top"
            (onClick)="editEndpoint(endpoint)" />
          <p-button
            icon="pi pi-trash"
            severity="secondary"
            [text]="true"
            [rounded]="true"
            size="small"
            [disabled]="!canDeleteWebhook()"
            [pTooltip]="
              canDeleteWebhook()
                ? ('GENERAL.DELETE' | translate)
                : ('GENERAL.NO_PERMISSION' | translate)
            "
            tooltipPosition="top"
            (onClick)="deleteEndpoint(endpoint)" />
        </div>
      </ng-template>
    </tf-data-table>
  }

  <!-- Webhook Dialog (Create/Edit) -->
  <tf-settings-webhook-dialog
    [visible]="isDialogVisible()"
    [endpoint]="editingEndpoint()"
    [eventTypes]="eventTypes()"
    [eventTypeOptions]="eventTypeOptions()"
    [onSave]="handleDialogSave"
    (cancelled)="closeDialog()"
    (secretAcknowledged)="handleSecretAcknowledged()"
    (visibilityChange)="isDialogVisible.set($event)" />

  <p-confirmDialog />
</div>
